import type Konva from "konva";
import { Group, Rect, Text } from "react-konva";
import { STICKY_PALETTE, type StickyNote } from "./snapshot.js";

export interface StickiesLayerProps {
  stickies: readonly StickyNote[];
  selectedIds: ReadonlySet<string>;
  /** Set to true when the select tool is active. Disables drag otherwise. */
  draggable: boolean;
  onSelect: (id: string) => void;
  /** Fired when a drag gesture starts — used by editors to snapshot the
   * positions of every co-selected entity for group drag. */
  onDragStart?: (id: string, x: number, y: number) => void;
  onDragMove: (id: string, x: number, y: number) => void;
  onDragEnd: (id: string, x: number, y: number) => void;
  /** Double-tap / double-click → open editor dialog. */
  onEdit: (id: string) => void;
}

/**
 * Whiteboard sticky-note layer.
 *
 * Each sticky is a rounded paper rect + ink text, painted as a single Konva
 * `Group` so the user can drag it as one unit. Selection adds an outer
 * highlight ring that uses the same lime accent as the rest of the editor.
 */
export function StickiesLayer({
  stickies,
  selectedIds,
  draggable,
  onSelect,
  onDragStart,
  onDragMove,
  onDragEnd,
  onEdit,
}: StickiesLayerProps) {
  return (
    <>
      {stickies.map((s) => {
        const palette = STICKY_PALETTE[s.color];
        const selected = selectedIds.has(s.id);
        return (
          <Group
            key={s.id}
            id={`wb-sticky-${s.id}`}
            x={s.x}
            y={s.y}
            draggable={draggable}
            onMouseDown={(e: Konva.KonvaEventObject<MouseEvent>) => {
              e.cancelBubble = true;
              // Don't replace a multi-selection when grabbing an already
              // selected sticky — the editor needs the full selection alive
              // to start a group drag.
              if (!selected) onSelect(s.id);
            }}
            onTouchStart={(e: Konva.KonvaEventObject<TouchEvent>) => {
              e.cancelBubble = true;
              if (!selected) onSelect(s.id);
            }}
            onDblClick={() => onEdit(s.id)}
            onDblTap={() => onEdit(s.id)}
            onDragStart={(e) => onDragStart?.(s.id, e.target.x(), e.target.y())}
            onDragMove={(e) => onDragMove(s.id, e.target.x(), e.target.y())}
            onDragEnd={(e) => onDragEnd(s.id, e.target.x(), e.target.y())}
          >
            {/* Drop-shadow halo for the lifted post-it feel. */}
            <Rect
              x={6}
              y={8}
              width={s.w}
              height={s.h}
              fill="rgba(0,0,0,0.35)"
              cornerRadius={6}
              listening={false}
            />
            <Rect
              x={0}
              y={0}
              width={s.w}
              height={s.h}
              fill={palette.paper}
              stroke={selected ? "#97fc00" : palette.stroke}
              strokeWidth={selected ? 3 : 1.5}
              cornerRadius={6}
            />
            <Text
              x={12}
              y={12}
              width={s.w - 24}
              height={s.h - 24}
              text={s.value || "Tippe zum Bearbeiten"}
              fontSize={s.fontSize}
              fontStyle="600"
              fill={s.value ? palette.ink : "rgba(0,0,0,0.35)"}
              listening={false}
              wrap="word"
            />
          </Group>
        );
      })}
    </>
  );
}
